import { Component, Vue } from "vue-property-decorator";
import { mapState } from "vuex";
import { Moment } from "moment";
import { 
  Icon, 
  Options,
  ChartData,
  Assessment,
  AlertZone,
  Biometrics,
  AssessmentTypes,
  AssessmentResult,
  AssessmentChoice,
  AssessmentHistory,
  BasicNeeds
} from "@/views/dashboard/dashboard.types";
import { ClientDDL, ClientInfo } from "@/views/base/base.types";
import BiometricResult from "@/views/biometric-result.vue";
import { PatientSummary } from "@/views/summary/summary.types";

@Component({
  computed: {
    ...mapState("Dashboard", [
      "phq9ChartOptions",
      "gad7ChartOptions",
      "sowsChartOptions",
      "cravingsChartOptions",
      "appointmentIcons",
      "selectedPatientDDL",
      "assessmentResults",
      "assessmentChoices",
      "assessmentHistory",
      "utoxIcons",
      "labels",
      "phq9ChartData",
      "gad7ChartData",
      "sowsChartData",
      "cravingsChartData",
      "noOfDays",
      "startDate",
      "endDate",
      "phq9Assessment",
      "gad7Assessment",
      "sowsAssessment",
      "cravingsAssessment",
      "phq9AlertZones",
      "gad7AlertZones",
      "sowsAlertZones",
      "cravingsAlertZones",
      "biometrics",
      "biometricResults",
      "basicNeeds"
    ]),
    ...mapState("Base", [
      "selectedClientDDL",
      "clientInfo"
    ]),
    ...mapState("Summary", [
      "selectedPatientSummary"
    ])
  }
})
export default class DashboardMixin extends Vue {
  clientInfo!: ClientInfo[];
  appointmentIcons!: Icon[];
  utoxIcons!: Icon[];
  labels!: string[][];
  phq9AlertZones!: AlertZone[];
  gad7AlertZones!: AlertZone[];
  sowsAlertZones!: AlertZone[];
  cravingsAlertZones!: AlertZone[];
  assessmentResults!: AssessmentResult[];
  assessmentChoices!: AssessmentChoice[];
  assessmentHistory!: AssessmentHistory[];
  phq9ChartData!: ChartData;
  gad7ChartData!: ChartData;
  sowsChartData!: ChartData;
  cravingsChartData!: ChartData;
  phq9ChartOptions!: Options;
  gad7ChartOptions!: Options;
  sowsChartOptions!: Options;
  cravingsChartOptions!: Options;
  phq9Assessment!: Assessment;
  gad7Assessment!: Assessment;
  sowsAssessment!: Assessment;
  cravingsAssessment!: Assessment;
  noOfDays!: number;
  startDate!: Moment;
  endDate!: Moment;
  selectedClientDDL!: ClientDDL;
  biometrics!: Biometrics[];
  biometricResults!: BiometricResult[];
  selectedPatientSummary!: PatientSummary;
  basicNeeds!: BasicNeeds;

  pickDate = new Date();

  metricIndex = [
    { icon: 'fa-bed', title: 'Sleep Duration', max: 'sleepDuration', min: 'sleepDuration'},
    { icon: 'fa-heartbeat', title: 'Heart Rate', max: 'maxBpm', min: 'minBpm' },
    { icon: 'fa-chart-line', title: 'Heart Rate Variability', max: 'maxHrMad', min: 'minHrMad' },
    { icon: 'fa-lungs', title: 'Respiratory Rate', max: 'maxBr', min: 'minBr' },
    { icon: 'fa-temperature-three-quarters', title: 'Temperature', max: 'maxTObject', min: 'minTObject'},
    { icon: 'fa-tint', title: 'SPO2', max: 'maxSpo2', min: 'minSpo2' }
  ]

  metricResultsIndex = [
    { icon: 'fa-bed', title: 'Sleep', value: 'minutesPerHour'},
    { icon: 'fa-heartbeat', title: 'Heart Rate', value: 'bpmAvg' },
    { icon: 'fa-chart-line', title: 'Heart Rate Variability', value: 'hrMadAvg' },
    { icon: 'fa-lungs', title: 'Respiratory Rate', value: 'brAvg' },
    { icon: 'fa-temperature-three-quarters', title: 'Temperature', value: 'tObjectAvg' },
    { icon: 'fa-tint', title: 'SPO2', value: 'spo2Avg' }
  ]

  get selectedName() {
    return `${this.selectedPatientSummary.firstName} ${this.selectedPatientSummary.lastName}`;
  }

  get selectedClient() {
    return this.clientInfo.find(
      item => item.clientId === this.selectedClientDDL.value
    ) ?? new ClientInfo;
  }

  getInitials(name: string): string {
    if (!name) return '';
    const words = name.split(' ');
    return words.length >= 2
      ? words[0][0] + words[1][0]
      : words[0][0];
  }

  getBasicNeedColor(value: string | undefined) {
    if (!value) return '#CCCCCC';
    return value.toLowerCase() === 'yes' ? 'green' : 'red';
  }

  getAssessentType(assessmentType: string): number {
    switch (assessmentType) {
      case "PHQ-9":
        return AssessmentTypes.PHQ9;
      case "GAD-7":
        return AssessmentTypes.GAD7;
      case "SOWS":
          return AssessmentTypes.SOWS;
      case "BAM":
          return AssessmentTypes.BAM;
      default:
        return 0;
    }
  }
}